<template>
  <CustomModal
    :id="`item-task-deleted-${comment.id}`"
    modalClass="custom-modal_margin-custom is-large modal-delete-task-custom"
  >
    <template #header>
      <div class="header-container">
        <CommentOwner
          v-if="comment.owner"
          :user="comment.owner"
          class="user"
          userNameWeight="400"
          size="large"
        >
          <template #username="{ userName }">
            {{ userName }}
            <span class="comment__action">{{ comment.subtitleLabel }}</span>
          </template>
        </CommentOwner>

        <span class="item-task-deleted-time">
          {{ timeAndDateReversedWithAt }}
        </span>
      </div>
    </template>

    <template #body>
      <TaskItem
        :task="beforeTask"
        :customDarkBgColor="'var(--accent-200)'"
        :customLightThemeBgColor="'var(--accent-03)'"
        :isOpen="true"
        class="pointer-none modal-task-deleted"
      />

      <div
        v-if="beforeTask?.structuredDescription?.getTextFromHtml"
        class="item-content-changed__description custom-scroll"
      >
        <Editor
          id="beforeTask"
          type="read"
          :extensions="extensions"
          :value="beforeTask?.structuredDescription?.html"
          class="modal-delete-task-editor"
        />
      </div>
    </template>

    <template #footer>
      <ControlButtons
        :hideCancel="true"
        @save="hideModal"
      >
        <template #save> Ok </template>
      </ControlButtons>
    </template>
  </CustomModal>
</template>

<script setup lang="ts">
import TaskItemExtension from "@tiptap/extension-task-item"
import TaskListExtension from "@tiptap/extension-task-list"
import { computed, getCurrentInstance } from "vue"

import ControlButtons from "@/components/ControlButtons/index.vue"
import Editor from "@/components/Editor/index.vue"
import CustomModal from "@/components/Modal/index.vue"
import TaskItem from "@/components/Task/TaskItem.vue"
import CommentOwner from "@/components/User/index.vue"

import { Comment, Task } from "@/models"
import { ReferenceAllUser, ReferenceAt } from "@/plugins/extensions/reference/referenceAt"
import {
  ReferenceHashEpic,
  ReferenceHashFile,
  ReferenceHashIdea,
  ReferenceHashItem,
  ReferenceHashMilestone,
  ReferenceHashProject,
  ReferenceHashSprint,
  ReferenceHashTask,
  ReferenceHashTeam,
  ReferenceHashTip
} from "@/plugins/extensions/reference/referenceHash"
import { ReferenceLink } from "@/plugins/extensions/reference/referenceLink"
import { formatDate } from "@/utils/dateUtil"

import type { PropType } from "vue"

const props = defineProps({
  comment: {
    type: Object as PropType<Comment>,
    required: true
  }
})

const { proxy } = getCurrentInstance() || {}

const beforeTask = computed(() => {
  return Task.create(props.comment.itemAuditRecord?.taskBefore ?? undefined)
})

const hideModal = () => {
  if (proxy && proxy.$modal) {
    proxy.$modal.hide(`item-task-deleted-${props.comment.id}`)
  }
}

const timeAndDateReversedWithAt = computed(() => {
  const datePart = formatDate(props.comment.createdDate, "MM/dd/yy")
  const timePart = formatDate(props.comment.createdDate, "h:mm bb")
  return `${datePart} at ${timePart}`
})

const extensions = computed(() => {
  return [
    ReferenceAt,
    ReferenceHashProject,
    ReferenceHashMilestone,
    ReferenceHashEpic,
    ReferenceHashIdea,
    ReferenceHashTeam,
    ReferenceHashSprint,
    ReferenceHashItem,
    ReferenceHashTask,
    ReferenceHashTip,
    ReferenceLink,
    ReferenceHashFile,
    TaskListExtension.configure({
      HTMLAttributes: {
        class: "custom-check-list"
      }
    }),
    TaskItemExtension.configure({
      nested: true,
      HTMLAttributes: {
        class: "custom-check-list__item"
      }
    }),
    ReferenceAllUser
  ]
})
</script>

<style scoped lang="scss">
@import "@/assets/scss/utils/scroll";

:deep(.custom-modal) {
  width: 650px;

  @media (max-width: 1536px) {
    width: 482px;
  }

  @media (max-width: 180px) {
    width: 466px;
  }
}

:deep(.custom-editor__content) {
  max-height: 200px;
  margin-right: -6px;
}

:deep(.modal-delete-task-editor .custom-editor__content) {
  @extend .custom-scroll;
}

:deep(.task.pointer-none.modal-task-deleted) {
  @media (max-width: 1536px) {
    width: 104%;
  }
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--step2);
}

:deep(.user__content) {
  > div:first-child {
    font-size: 20px;
    font-weight: 400;
    line-height: 20px;

    @media (max-width: 1536px) {
      font-size: 16px;
    }
  }
}

.item-task-deleted-title {
  font-weight: bold;
}

.item-task-deleted-time {
  color: var(--monochrome-05);
  font-size: 16px;

  @media (max-width: 1536px) {
    font-size: 14px;
  }
}

.item-content-changed__description {
  padding: var(--step4);
  padding-right: 0;
  border: 1px solid var(--monochrome-03);
  border-radius: var(--radius-item);

  :deep(#beforeTask) {
    font-size: 16px;

    @media (max-width: 1536px) {
      font-size: 14px;
    }

    @media (max-width: 1280px) {
      font-size: 12px;
    }
  }
}

:deep(.task__name .task__input) {
  font-size: 16px;

  @media (max-width: 1536px) {
    font-size: 14px;
  }

  @media (max-width: 1280px) {
    font-size: 12px;
  }
}

:deep(.task__inner) {
  display: grid;
  gap: var(--step4);
  grid-template-columns: 28px 1fr 58px 46px 32px 0;
}

.dark {
  .item-content-changed__description {
    border-color: var(--monochrome-22);
    background: var(--monochrome-20);
  }
}
</style>
